<script>
	// NPM Modules
	import { createEventDispatcher } from 'svelte';
	import { collect } from 'collect.js';
	// Stores
	import SportStore from '@/Stores/Sport/SportStore';
	import MultiGroupStore from '@/Stores/Sport/MultiGroupStore';
	import DerivedMarketTemplateStore from '@/Stores/Sport/DerivedMarketTemplateStore.js';
	import DerivedMarketGroupStore from '@/Stores/Sport/DerivedMarketGroupStore.js';
	import ParentMarketStore from '@/Stores/Sport/ParentMarketStore';
	// Components
	import Modal from '@/Components/Modal.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	import SelectInput from '@/Components/SelectInput.svelte';
	import TextInput from '@/Components/TextInput.svelte';
	import Icon from '@/Components/Icon.svelte';
	import TextAreaInput from '@/Components/TextAreaInput.svelte';
	import Switch from '@/Components/Switch.svelte';
	// Services
	import { notify } from '@/Services/Notify';
	import { nextLadderPrice } from '@/Services/Helpers.js';
	// Partials
	import LadderPrice from '@/Partials/Sport/Markets/_LadderPrice.svelte';
	// Constants
	import { PriceLadder } from '@/Constants/Prices.js';

	window.nextLadderPrice = nextLadderPrice;
	window.PriceLadder = PriceLadder;

	const dispatch = createEventDispatcher();

	export let open;
	export let sport;
	export let derivedMarketTemplateId = 'create';

	let sending = false;
	let loading = true;
	let selectedSport = sport.id;
	let errors = null;
	let pricingMatrixString = '';
	let onlyShowManualParentMarkets = true;

	SportStore.getSports() && derivedMarketTemplateId === 'create'
		? DerivedMarketTemplateStore.newDerivedMarketTemplate(sport)
		: '';

	$: pricingEntriesCount = overrideFormData && overrideFormData.price_matrix && overrideFormData.price_matrix.length;
	$: selectedSport && selectedSportChanged();

	$: derivedMarketTemplateId && getDerivedMarketTemplate(derivedMarketTemplateId);
	$: derivedMarketTemplate =
		derivedMarketTemplateId &&
		$DerivedMarketTemplateStore &&
		$DerivedMarketTemplateStore.firstWhere('id', derivedMarketTemplateId);
	$: derivedMarketTemplate && updateLocalFormData();
	$: filteredParentMarkets =
		$ParentMarketStore &&
		(onlyShowManualParentMarkets || !onlyShowManualParentMarkets) &&
		filterParentMarkets($ParentMarketStore.sortBy('name'));

	let overrideFormData = {
		name: null,
		parent_market_id: null,
		price_matrix: []
	};

	$: open && resetData();

	function filterParentMarkets(parentMarketsToBeFiltered) {
		let parentMarketsToFilter = collect(parentMarketsToBeFiltered);

		if (onlyShowManualParentMarkets) {
			parentMarketsToFilter = parentMarketsToFilter.where('is_manual', true);
		}

		parentMarketsToFilter = parentMarketsToFilter.where('market_type.rolling_type', null);

		return parentMarketsToFilter.all();
	}

	function resetData() {
		overrideFormData = {
			name: null,
			parent_market_id: null,
			price_matrix: []
		};
		if (derivedMarketTemplate) {
			updateLocalFormData();
		}
	}

	function getDerivedMarketTemplate(derivedMarketTemplateId) {
		if (derivedMarketTemplateId !== 'create') {
			DerivedMarketTemplateStore.getDerivedMarketTemplate(derivedMarketTemplateId);
		}
	}

	function updateLocalFormData() {
		if (derivedMarketTemplate.name !== null) {
			overrideFormData.name = derivedMarketTemplate.name;
		}
		if (derivedMarketTemplate.parent_market_id !== null) {
			overrideFormData.parent_market_id = derivedMarketTemplate.parent_market_id;
		}
		if (derivedMarketTemplate.price_matrix && derivedMarketTemplate.price_matrix.length !== 0) {
			if (derivedMarketTemplate.price_matrix !== null) {
				overrideFormData.price_matrix = derivedMarketTemplate.price_matrix;
				overrideFormData.price_matrix = overrideFormData.price_matrix;
			} else {
				overrideFormData.price_matrix = [];
			}
		} else {
			overrideFormData.price_matrix = [];
		}
		overrideFormData = overrideFormData;
	}

	const addPricingRow = () => {
		overrideFormData.price_matrix.push({
			id: overrideFormData.price_matrix.length + 1,
			price: 100,
			derived_price: 100
		});

		// Trick reactivity!
		overrideFormData.price_matrix = overrideFormData.price_matrix;
	};

	function deletePricingRow(marketPricingId) {
		for (const marketPricingIndex in overrideFormData.price_matrix) {
			if (marketPricingId.toString() === overrideFormData.price_matrix[marketPricingIndex].id.toString()) {
				overrideFormData.price_matrix.splice(parseInt(marketPricingIndex.toString()), 1);

				// Trick Reactivity
				overrideFormData.price_matrix = overrideFormData.price_matrix;
			}
		}
	}

	async function selectedSportChanged() {
		loading = true;
		await ParentMarketStore.getParentMarketsForSport(selectedSport).finally(() => (loading = false));
	}

	function createDerivedMarketTemplate() {
		sending = true;
		loading = true;
		DerivedMarketTemplateStore.createDerivedMarketTemplate(
			sport.id,
			derivedMarketTemplate.preparePayload(overrideFormData)
		)
			.then(() => {
				notify({
					type: 'success',
					title: 'Derived market template created',
					message: 'Derived market template successfully created!',
					timeout: 3
				});
				open = false;
				dispatch('created');
			})
			.catch((e) => {
				errors = e;
				notify({
					type: 'danger',
					title: 'Error!',
					message: 'Problem creating new derived market template',
					timeout: 5
				});
			})
			.finally(() => {
				sending = false;
				loading = false;
			});
	}

	function updateDerivedMarketTemplate() {
		sending = true;
		loading = true;
		DerivedMarketTemplateStore.updateDerivedMarketTemplate(derivedMarketTemplate, overrideFormData)
			.then(() => {
				notify({
					type: 'success',
					title: 'Derived market template updated',
					message: 'Derived market template successfully updated!',
					timeout: 3
				});
				open = false;
				dispatch('updated');
			})
			.catch((e) => {
				errors = e;
				notify({
					type: 'danger',
					title: 'Error!',
					message: 'Problem updating derived market template',
					timeout: 5
				});
			})
			.finally(() => {
				sending = false;
				loading = false;
			});
	}

	function setPricingEntriesForMatrix(value) {
		if (value === overrideFormData.price_matrix.length) {
			return;
		}

		if (value > overrideFormData.price_matrix.length) {
			let numberToCreate = value - overrideFormData.price_matrix.length;
			for (let i = 0; i < numberToCreate; i++) {
				overrideFormData.price_matrix.push({
					id: overrideFormData.price_matrix.length + 1,
					price: 100,
					derived_price: 100
				});
			}
			overrideFormData.price_matrix = overrideFormData.price_matrix;
		}
	}

	function addPricingEntryToMatrix(price = 100, derived_price = 100) {
		overrideFormData.price_matrix = [
			...overrideFormData.price_matrix,
			{
				id: overrideFormData.price_matrix.length + 1,
				price: price,
				derived_price: derived_price
			}
		];
	}

	function createPricingMatrixFromString(stringSent) {
		// eslint-disable-next-line no-useless-escape
		const regexp = /^\s*([\d.]+)\s+([\d.]+)\s*$/gm;
		const pricingEntries = [...stringSent.matchAll(regexp)];
		let pricingEntriesToAdd = [];
		for (let index in pricingEntries) {
			pricingEntriesToAdd.push({
				price: pricingEntries[index][1],
				derived_price: pricingEntries[index][2]
			});
		}

		let canAddToExisting = false;
		if (overrideFormData.price_matrix.length > 0) {
			canAddToExisting = true;
		}
		for (const pricingEntry of pricingEntriesToAdd) {
			if (canAddToExisting) {
				let added = false;
				for (let index in overrideFormData.price_matrix) {
					if (
						overrideFormData.price_matrix[index].price === 100 &&
						overrideFormData.price_matrix[index].derived_price === 100
					) {
						overrideFormData.price_matrix[index].price = parseInt(
							fixPrecision(parseFloat(pricingEntry.price) * 100).toFixed(0)
						);
						overrideFormData.price_matrix[index].derived_price = parseInt(
							fixPrecision(parseFloat(pricingEntry.derived_price) * 100).toFixed(0)
						);
						added = true;
						break;
					}
				}
				if (!added) {
					canAddToExisting = false;
				} else {
					continue;
				}
			}
			addPricingEntryToMatrix(
				parseInt(fixPrecision(parseFloat(pricingEntry.price) * 100).toFixed(0)),
				parseInt(fixPrecision(parseFloat(pricingEntry.derived_price) * 100).toFixed(0))
			);
		}
	}

	function fixPrecision(num) {
		return Math.round((num + Number.EPSILON) * 100) / 100;
	}
</script>

{#if open}
	<Modal
		on:close={() => {
			open = false;
			DerivedMarketGroupStore.getDerivedMarketGroupsForSport(derivedMarketTemplate.sport.id);
		}}
		className="md:w-2/3 lg:w-2/3 md:mx-auto"
	>
		<h2 slot="header" class="text-xl font-bold text-center">
			{#if derivedMarketTemplateId == 'create'}
				Create a new derived market template for {sport.name}
			{:else}
				Update derived market template - {derivedMarketTemplate.name}
			{/if}
		</h2>
		<div class="flex flex-col w-full px-2 pt-2 text-center">
			{#if $SportStore && $MultiGroupStore}
				<div class="px-2 pt-2 bg-gray-100 rounded pb-0 w-full mb-2">
					<div class="flex flex-col w-full min-h-104">
						<div class="flex w-1/3 text-left">
							<TextInput
								name="name"
								label="Derived market template name"
								className="py-4 w-full"
								placeholder="Enter derived market template name"
								errors={errors && errors.name}
								bind:value={overrideFormData.name}
							/>
						</div>
						<div class="flex text-left flex-shrink w-2/3">
							<div class="flex flex-row w-1/2">
								<SelectInput
									label="Select parent market to be used when market is created"
									className="py-4 w-full text-left"
									errors={errors && errors.parent_market_id}
									name="marketParentMarketId"
									placeholder="Select parent market to use"
									disabled={loading}
									required={true}
									bind:value={overrideFormData.parent_market_id}
									on:change={(e) => {
										const parentMarket = $ParentMarketStore.firstWhere('id', String(e.target.value));
										if (parentMarket) {
											overrideFormData.name = parentMarket.name;
										}
									}}
								>
									<option value={null} selected disabled>Select parent market to use</option>
									{#if overrideFormData.parent_market_id !== null}
										{#each $ParentMarketStore
											.where('id', String(overrideFormData.parent_market_id))
											.all() as parentMarket (parentMarket.id)}
											<option value={parseInt(parentMarket.id)} selected>
												{parentMarket.name}: [{parentMarket.market_type.name}]
											</option>
										{/each}
									{/if}
									{#each filteredParentMarkets as parentMarket (parentMarket.id)}
										<option value={parseInt(parentMarket.id)}>
											{parentMarket.name}: [{parentMarket.market_type.name}]
										</option>
									{/each}
								</SelectInput>
							</div>
							<div class="flex flex-row w-1/2 py-4 pt-12 mt-1 pl-6">
								<Switch
									name="onlyShowManualMarkets"
									on:change={() => {
										onlyShowManualParentMarkets = !onlyShowManualParentMarkets;
									}}
									checked={onlyShowManualParentMarkets}
									className="-mt-1"
								/>
								<div class="ml-3">Only show manual markets</div>
							</div>
						</div>
						<div class="mt-4">
							<div class="flex flex-row items-center">
								<div class="mr-4">Entries to add:</div>
								<div class="flex flex-row border border-gray-400">
									<input
										on:change={(event) => setPricingEntriesForMatrix(event.target.value)}
										on:keyup={(event) => {
											if (event.keyCode === 13) {
												setPricingEntriesForMatrix(event.target.value);
											}
										}}
										disabled={loading}
										type="number"
										class="w-10 text-xs text-center border-gray-400 focus:outline-none"
										value={pricingEntriesCount}
									/>
									<button
										disabled={loading}
										on:click|stopPropagation={() => addPricingRow()}
										class="p-1 font-semibold text-white bg-gray-500 cursor-pointer focus:outline-none hover:bg-gray-400"
									>
										<span class="m-auto">+</span>
									</button>
								</div>
							</div>
							<div class="font-light text-sm mt-1">These entries will be added to the pricing matrix.</div>
							<div class="flex flex-row items-center justify-center">
								<TextAreaInput
									rows="3"
									disabled={loading}
									name="pasteFromString"
									className="py-4 w-1/2 text-left"
									placeholder="Paste pricing entries text here."
									bind:value={pricingMatrixString}
								/>
								<LoadingButton
									on:click={() => createPricingMatrixFromString(pricingMatrixString)}
									loading={false}
									disabled={loading}
									className="ml-2 my-4 btn btn-primary"
								>
									Generate pricing entries
								</LoadingButton>
							</div>
						</div>
						<div class="flex flex-row items-center justify-start">
							<h1 class="pb-1">Derived market template price matrix</h1>
						</div>
						<hr />
						<div class="flex flex-row w-full">
							<table class="w-full">
								{#if overrideFormData.price_matrix?.length > 0}
									<thead>
										<tr>
											<th class="w-1/3"> Market price </th>
											<th class="w-1/3"> Derived price </th>
											<th class="w-1/3"></th>
										</tr>
									</thead>
								{/if}
								{#each overrideFormData?.price_matrix ?? [] as marketPrice (marketPrice.id)}
									<tbody>
										<tr>
											<td>
												<div class="flex flex-row justify-center items-center">
													<LadderPrice bind:item={marketPrice} tabindex={marketPrice.id * 10} priceKey="price" />
												</div>
											</td>
											<td
												><div class="flex flex-row justify-center items-center">
													<LadderPrice
														bind:item={marketPrice}
														tabindex={marketPrice.id * 10 + 1}
														priceKey="derived_price"
													/>
												</div>
											</td>
											<td>
												<div class="flex flex-row justify-end items-center mr-4">
													<button
														class="btn btn-danger flex flex-row w-8 h-8 items-center justify-center p-1.5"
														on:click={() => deletePricingRow(marketPrice.id)}
													>
														<Icon name="bin" />
													</button>
												</div>
											</td>
										</tr>
									</tbody>
								{:else}
									<tbody>
										<tr><td colspan="3" class="py-2"> No pricing has been entered </td></tr>
									</tbody>
								{/each}
							</table>
						</div>
						<hr />
						<div class="flex flex-row items-center justify-start text-xs">
							<h1 class="pb-1">
								The highest market price that is &lt;= the current selection price will be assigned the corresponding
								derived price
							</h1>
						</div>
						<div class="flex flex-row w-full justify-end pr-4 mt-2">
							<button
								disabled={loading}
								class="btn btn-primary font-semibold cursor-pointer focus:outline-none"
								on:click={addPricingRow}
							>
								<span class="m-auto">Add pricing entry</span>
							</button>
						</div>
					</div>
				</div>
			{/if}

			<LoadingButton
				on:click={() => {
					if (derivedMarketTemplateId == 'create') {
						createDerivedMarketTemplate();
					} else {
						updateDerivedMarketTemplate();
					}
				}}
				loading={sending}
				className="mb-4 btn btn-primary"
			>
				{#if derivedMarketTemplateId == 'create'}
					Create derived market template
				{:else}
					Update derived market template
				{/if}
			</LoadingButton>
			<button class="mb-4 btn btn-muted" on:click={() => (open = false)}>Cancel</button>
		</div>
	</Modal>
{/if}
